import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CONTAINER_DELIVERIES_SERVICE_NAME,
  FAT_BONES_SERVICE_NAME,
  GREASE_TRAP_SERVICE_NAME,
  HOOD_CLEANING_SERVICE_NAME,
  OIL_DISPOSAL_SERVICE_NAME,
} from '../../config/constants';
import { ReactComponent as ArrowDown } from '../../icons/arrowdown2.svg';
import { ReactComponent as Bone } from '../../icons/bone.svg';
import { ReactComponent as ContainerDeliveries } from '../../icons/container-deliveries.svg';
import { ReactComponent as GreaseTrap } from '../../icons/grease-trap.svg';
import { ReactComponent as HoodCleaning } from '../../icons/hood-cleaning.svg';
import { ReactComponent as OilIcon } from '../../icons/Oil.svg';
import type { FutureRoute } from '../../services/recoil/atoms/futureRoutesState';

interface Props {
  route: FutureRoute;
}

function formatRouteDate(dateStr: string): string {
  // Strip any time component, then append noon so the date is parsed as local
  // time (not UTC midnight, which would shift the day in negative UTC offsets).
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const date = new Date(`${datePart}T12:00:00`);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function FutureRouteCard({ route }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const getServiceIcon = () => {
    switch (route.service) {
      case HOOD_CLEANING_SERVICE_NAME:
        return <HoodCleaning className="h-5 w-5" />;
      case GREASE_TRAP_SERVICE_NAME:
        return <GreaseTrap className="h-5 w-5" />;
      case OIL_DISPOSAL_SERVICE_NAME:
        return <OilIcon className="h-5 w-5" />;
      case FAT_BONES_SERVICE_NAME:
        return <Bone className="h-5 w-5" />;
      case CONTAINER_DELIVERIES_SERVICE_NAME:
        return <ContainerDeliveries className="h-5 w-5" />;
      default:
        return <OilIcon className="h-5 w-5" />;
    }
  };

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* Header — tap to expand/collapse */}
      <button
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-medium text-gray-500">
            {formatRouteDate(route.date)}
          </span>
          <span className="text-sm font-bold text-ReyesBlack">
            {route.route_name}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-600">
            {getServiceIcon()}
            {route.service}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-ReyesBlack">
            {route.stop_count ?? route.stops?.length ?? 0}{' '}
            {t('dashboard.futureRoute.stops')}
          </span>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
            {t('dashboard.futureRoute.viewOnly')}
          </span>
          <ArrowDown
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {/* Expandable stops list */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-3 pt-2">
          {route.stops && route.stops.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {route.stops.map((stop, index) => {
                const isContainerReplacement =
                  stop.serviceStop === CONTAINER_DELIVERIES_SERVICE_NAME &&
                  route.service !== CONTAINER_DELIVERIES_SERVICE_NAME;
                return (
                  <li key={stop.id} className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                      {index + 1}
                    </span>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-ReyesBlack">
                        {stop.clientName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {stop.clientAddress}
                      </span>
                      {isContainerReplacement && (
                        <span className="flex w-fit items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                          <ContainerDeliveries className="h-3 w-3" />
                          {t('dashboard.futureRoute.containerReplacement')}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-gray-400">
              {t('dashboard.futureRoute.noStops')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default FutureRouteCard;
